import React, { useState, useEffect } from 'react'; // ✅ Added useEffect
import { Link, useNavigate, useLocation } from 'react-router-dom'; // ✅ Added useLocation
import { Mail, KeyRound, Lock } from 'lucide-react';
import { apiForgot, apiReset } from '../services/auth';
import useSeo from '../hooks/useSeo';

export default function Forgot() {
  useSeo({ title: 'Forgot password', description: 'Reset your Maa Florist Mumbai account password.', canonical: '/forgot' });
  
  const [step, setStep] = useState(1);
  const [login, setLogin] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [devOtp, setDevOtp] = useState(null);
  const [busy, setBusy] = useState(false);
  
  const nav = useNavigate();
  const location = useLocation(); // ✅ Get location object

  // ✅ NEW: Auto-fill email if passed from Login page
  useEffect(() => {
    if (location.state?.email) {
      setLogin(location.state.email);
    }
  }, [location.state]);

  const request = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      const res = await apiForgot({ email:login });
      setMsg(res.message);
      setDevOtp(res.dev_otp || null);
      setStep(2);
    } catch (e) { setErr(e.message || 'Something went wrong'); }
    finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    setErr(null);
    if (password !== confirm) { setErr('Passwords do not match'); return; }
    setBusy(true);
    try {
      await apiReset({ email:login, otp, password });
      nav('/login', { state: { flash: 'Password updated. Please log in.' } });
    } catch (e) { setErr(e.message || 'Reset failed'); }
    finally { setBusy(false); }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] grid place-items-center bg-[#f6f2ea] px-4 py-12">
      <div className="bg-white w-full max-w-md rounded-xl shadow-sm border border-neutral-200 p-8">
        <div className="text-center mb-6">
          <h1 className="font-serif text-3xl text-neutral-900">Forgot password</h1>
          <p className="text-sm text-neutral-600 mt-1">Enter your registered email — we&#39;ll send a 6-digit OTP to reset your password.</p>
        </div>

        {msg && <div className="mb-4 text-sm bg-green-50 border border-green-200 text-green-700 rounded-md px-3 py-2">{msg}</div>}
        {devOtp && <div className="mb-4 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded-md px-3 py-2">Dev OTP (mock only): <b>{devOtp}</b></div>}
        {err && <div className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">{err}</div>}

        {step === 1 && (
          <form onSubmit={request}>
            <label className="text-xs font-medium text-neutral-600 flex items-center gap-1"><Mail className="w-3 h-3" /> Email address *</label>
            <input required type="email" value={login} onChange={(e) => setLogin(e.target.value)} placeholder="you@example.com" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
            <button disabled={busy} className="mt-6 w-full bg-[#1e3a2b] hover:bg-[#2a5040] disabled:opacity-60 text-white font-bold py-3 rounded-md tracking-wide transition-colors">
              {busy ? 'Sending…' : 'Send OTP'}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={reset}>
            <label className="text-xs font-medium text-neutral-600 flex items-center gap-1"><KeyRound className="w-3 h-3" /> Enter OTP</label>
            <input required maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g,''))} className="mt-1 w-full tracking-widest text-center font-mono border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />

            <label className="mt-4 text-xs font-medium text-neutral-600 flex items-center gap-1"><Lock className="w-3 h-3" /> New password</label>
            <input required type="password" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />

            <label className="mt-4 text-xs font-medium text-neutral-600">Confirm new password</label>
            <input required type="password" minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />

            <button disabled={busy} className="mt-6 w-full bg-[#1e3a2b] hover:bg-[#2a5040] disabled:opacity-60 text-white font-bold py-3 rounded-md tracking-wide transition-colors">
              {busy ? 'Updating…' : 'Update password'}
            </button>
          </form>
        )}

        <div className="mt-6 text-sm text-center">
          <Link to="/login" className="text-[#1e3a2b] hover:underline">Back to login</Link>
        </div>
      </div>
    </main>
  );
}